import React, { useState, useCallback, useEffect } from 'react';
import { Button } from '@/shared/ui/button';
import { Card, CardContent } from '@/shared/ui/card';
import { Badge } from '@/shared/ui/badge';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { MapPin, Plus, X, Users, Type, Edit3 } from 'lucide-react';
import { useAreaTagsOptimistic } from '@/features/area-tags/hooks/useAreaTags';
import { useAreaTagMutations } from '@/features/area-tags/hooks/useAreaTagMutations';
import { usePhotoMarkers } from '../hooks/hooks';
import { ImageDimensions, Position, PhotoMarker } from '../types/types';
import { AreaTag } from '@/features/area-tags/types/area-tags';

/**
 * Point-in-polygon algorithm to check if a point is inside a polygon
 */
const isPointInPolygon = (point: [number, number], polygon: number[][]): boolean => {
  const [x, y] = point;
  let inside = false;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i];
    const [xj, yj] = polygon[j];

    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }

  return inside;
};

/**
 * Find markers that fall within a polygon
 */
const findMarkersInPolygon = (markers: PhotoMarker[], polygon: number[][]): PhotoMarker[] => {
  return markers.filter((marker) => isPointInPolygon([marker.x, marker.y], polygon));
};

/**
 * Calculate distance from a point to a line segment
 */
const distanceToLineSegment = (
  point: [number, number],
  lineStart: [number, number],
  lineEnd: [number, number]
): number => {
  const [px, py] = point;
  const [x1, y1] = lineStart;
  const [x2, y2] = lineEnd;

  const A = px - x1;
  const B = py - y1;
  const C = x2 - x1;
  const D = y2 - y1;

  const dot = A * C + B * D;
  const lenSq = C * C + D * D;
  let param = -1;
  if (lenSq !== 0) param = dot / lenSq;

  let xx, yy;
  if (param < 0) {
    xx = x1;
    yy = y1;
  } else if (param > 1) {
    xx = x2;
    yy = y2;
  } else {
    xx = x1 + param * C;
    yy = y1 + param * D;
  }

  const dx = px - xx;
  const dy = py - yy;
  return Math.sqrt(dx * dx + dy * dy);
};

/**
 * Find the closest point on a line segment to a given point
 */
const closestPointOnLineSegment = (
  point: [number, number],
  lineStart: [number, number],
  lineEnd: [number, number]
): [number, number] => {
  const [px, py] = point;
  const [x1, y1] = lineStart;
  const [x2, y2] = lineEnd;

  const A = px - x1;
  const B = py - y1;
  const C = x2 - x1;
  const D = y2 - y1;

  const dot = A * C + B * D;
  const lenSq = C * C + D * D;
  let param = -1;
  if (lenSq !== 0) param = dot / lenSq;

  if (param < 0) return [x1, y1];
  if (param > 1) return [x2, y2];
  return [x1 + param * C, y1 + param * D];
};

/**
 * Calculate distance between two points
 */
const distance = (point1: [number, number], point2: [number, number]): number => {
  const [x1, y1] = point1;
  const [x2, y2] = point2;
  return Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
};

const normalizePolygon = (points: number[][]): number[][] => {
  if (!Array.isArray(points) || points.length === 0) return [];
  const normalized = points
    .filter((p) => Array.isArray(p) && p.length === 2 && Number.isFinite(p[0]) && Number.isFinite(p[1]))
    .map((p) => [p[0], p[1]]);
  if (normalized.length > 1) {
    const [fx, fy] = normalized[0];
    const [lx, ly] = normalized[normalized.length - 1];
    if (fx === lx && fy === ly) return normalized.slice(0, -1);
  }
  return normalized;
};

const normalizePolygonPoints = (points: Array<[number, number] | { x: number; y: number }>): [number, number][] => {
  const normalized = points.map((p) => (Array.isArray(p) ? [p[0], p[1]] : [p.x, p.y])) as [number, number][];
  if (normalized.length > 1) {
    const [fx, fy] = normalized[0];
    const [lx, ly] = normalized[normalized.length - 1];
    if (fx === lx && fy === ly) return normalized.slice(0, -1);
  }
  return normalized;
};

const segmentsIntersect = (
  a1: [number, number],
  a2: [number, number],
  b1: [number, number],
  b2: [number, number]
): boolean => {
  const orient = (p: [number, number], q: [number, number], r: [number, number]) => {
    const v = (q[1] - p[1]) * (r[0] - q[0]) - (q[0] - p[0]) * (r[1] - q[1]);
    if (Math.abs(v) < 1e-10) return 0;
    return v > 0 ? 1 : 2;
  };
  const onSeg = (p: [number, number], q: [number, number], r: [number, number]) =>
    q[0] <= Math.max(p[0], r[0]) + 1e-10 &&
    q[0] >= Math.min(p[0], r[0]) - 1e-10 &&
    q[1] <= Math.max(p[1], r[1]) + 1e-10 &&
    q[1] >= Math.min(p[1], r[1]) - 1e-10;

  const o1 = orient(a1, a2, b1);
  const o2 = orient(a1, a2, b2);
  const o3 = orient(b1, b2, a1);
  const o4 = orient(b1, b2, a2);
  if (o1 !== o2 && o3 !== o4) return true;
  if (o1 === 0 && onSeg(a1, b1, a2)) return true;
  if (o2 === 0 && onSeg(a1, b2, a2)) return true;
  if (o3 === 0 && onSeg(b1, a1, b2)) return true;
  if (o4 === 0 && onSeg(b1, a2, b2)) return true;
  return false;
};

const polygonsOverlap = (polyA: [number, number][], polyB: [number, number][]): boolean => {
  if (polyA.length < 3 || polyB.length < 3) return false;
  const pointOnSegment = (p: [number, number], a: [number, number], b: [number, number]) => {
    const cross = (p[1] - a[1]) * (b[0] - a[0]) - (p[0] - a[0]) * (b[1] - a[1]);
    if (Math.abs(cross) > 1e-10) return false;
    const dot = (p[0] - a[0]) * (b[0] - a[0]) + (p[1] - a[1]) * (b[1] - a[1]);
    if (dot < -1e-10) return false;
    const lenSq = (b[0] - a[0]) ** 2 + (b[1] - a[1]) ** 2;
    if (dot - lenSq > 1e-10) return false;
    return true;
  };

  const strictSegmentsCross = (
    a1: [number, number],
    a2: [number, number],
    b1: [number, number],
    b2: [number, number]
  ) => {
    // Only treat proper crossings as overlap; shared boundaries/vertices are allowed.
    if (!segmentsIntersect(a1, a2, b1, b2)) return false;
    if (
      pointOnSegment(a1, b1, b2) ||
      pointOnSegment(a2, b1, b2) ||
      pointOnSegment(b1, a1, a2) ||
      pointOnSegment(b2, a1, a2)
    ) {
      return false;
    }
    return true;
  };

  for (let i = 0; i < polyA.length; i++) {
    const a1 = polyA[i];
    const a2 = polyA[(i + 1) % polyA.length];
    for (let j = 0; j < polyB.length; j++) {
      const b1 = polyB[j];
      const b2 = polyB[(j + 1) % polyB.length];
      if (strictSegmentsCross(a1, a2, b1, b2)) return true;
    }
  }

  const pointInPolygonStrict = (point: [number, number], polygon: [number, number][]) => {
    for (let i = 0; i < polygon.length; i++) {
      const a = polygon[i];
      const b = polygon[(i + 1) % polygon.length];
      if (pointOnSegment(point, a, b)) return false; // boundary contact is allowed
    }

    let inside = false;
    let j = polygon.length - 1;
    for (let i = 0; i < polygon.length; i++) {
      const [xi, yi] = polygon[i];
      const [xj, yj] = polygon[j];
      const intersect = (yi > point[1]) !== (yj > point[1]) &&
        point[0] < ((xj - xi) * (point[1] - yi)) / ((yj - yi) || 1e-12) + xi;
      if (intersect) inside = !inside;
      j = i;
    }
    return inside;
  };

  return pointInPolygonStrict(polyA[0], polyB) || pointInPolygonStrict(polyB[0], polyA);
};

interface EnhancedAreaTagsOverlayProps {
  selectedLayoutId: string;
  projectId: string;
  imageDimensions: ImageDimensions;
  position: Position;
  scale: number;
  svgRef: React.RefObject<SVGSVGElement>;
  groupRef: React.RefObject<SVGGElement>;
  onWheelZoom?: (e: React.WheelEvent) => void;
  onTouchStart?: (e: React.TouchEvent) => void;
  onTouchMove?: (e: React.TouchEvent) => void;
  onTouchEnd?: (e: React.TouchEvent) => void;
  onTextEditingModeChange?: (textEditingMode: boolean, editingAreaId?: number | null) => void;
  onSaveTextChanges?: () => void;
  onCancelTextChanges?: () => void;
  onPolygonEditingModeChange?: (polygonEditingMode: boolean) => void;
  pendingPolygonChanges?: Map<number, number[][]>;
}

/**
 * Enhanced drawing overlay with edge snapping for area creation
 */
const DrawingOverlay: React.FC<{
  isDrawing: boolean;
  imageDimensions: ImageDimensions;
  position: Position;
  scale: number;
  svgRef: React.RefObject<SVGSVGElement>;
  groupRef: React.RefObject<SVGGElement>;
  existingAreas: AreaTag[];
  onAreaCreated: (points: number[][]) => void;
  onWheelZoom?: (e: React.WheelEvent) => void;
  onTouchStart?: (e: React.TouchEvent) => void;
  onTouchMove?: (e: React.TouchEvent) => void;
  onTouchEnd?: (e: React.TouchEvent) => void;
}> = ({
  isDrawing,
  imageDimensions,
  position,
  scale,
  svgRef,
  groupRef,
  existingAreas,
  onAreaCreated,
  onWheelZoom,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
}) => {
  const [currentPolygon, setCurrentPolygon] = useState<number[][]>([]);
  const [hoveredPoint, setHoveredPoint] = useState<[number, number] | null>(null);
  const [snapInfo, setSnapInfo] = useState<{
    point: [number, number];
    type: 'vertex' | 'edge';
    areaId?: number;
  } | null>(null);
  const [isHorizontalConstrained, setIsHorizontalConstrained] = useState(false);
  const [isVerticalConstrained, setIsVerticalConstrained] = useState(false);
  const [isParallelAssistEnabled, setIsParallelAssistEnabled] = useState(false);
  const [isLengthAssistEnabled, setIsLengthAssistEnabled] = useState(false);
  const [parallelAssist, setParallelAssist] = useState<{
    referenceLineIndex: number;
    angle: number;
  } | null>(null);
  const [lengthAssist, setLengthAssist] = useState<{
    referenceLineIndex: number;
    length: number;
  } | null>(null);


  // Helper functions for geometric assistance
  const getLineAngle = useCallback((p1: [number, number], p2: [number, number]): number => {
    return Math.atan2(p2[1] - p1[1], p2[0] - p1[0]);
  }, []);

  const getLineLength = useCallback((p1: [number, number], p2: [number, number]): number => {
    return Math.sqrt(Math.pow(p2[0] - p1[0], 2) + Math.pow(p2[1] - p1[1], 2));
  }, []);

  const areAnglesParallel = useCallback((angle1: number, angle2: number, tolerance = 0.017): boolean => {
    // 0.017 radians ≈ 1 degree tolerance - MUCH stricter for parallel detection
    // Check if angles are parallel (either same direction or opposite direction)
    const getAngleDifference = (a1: number, a2: number) => {
      let diff = Math.abs(a1 - a2);
      if (diff > Math.PI) diff = 2 * Math.PI - diff;
      return diff;
    };
    
    const directDiff = getAngleDifference(angle1, angle2);
    const oppositeDiff = getAngleDifference(angle1, angle2 + Math.PI);
    
    return Math.min(directDiff, oppositeDiff) < tolerance;
  }, []);

  const getPointAtDistanceAndAngle = useCallback((
    start: [number, number], 
    distance: number, 
    angle: number
  ): [number, number] => {
    return [
      start[0] + distance * Math.cos(angle),
      start[1] + distance * Math.sin(angle)
    ];
  }, []);

  // Check for geometric assistance opportunities
  const findGeometricAssist = useCallback((currentPoint: [number, number]) => {
    if (currentPolygon.length === 0) {
      setParallelAssist(null);
      setLengthAssist(null);
      return currentPoint;
    }

    const lastPoint = currentPolygon[currentPolygon.length - 1] as [number, number];
    const currentAngle = getLineAngle(lastPoint, currentPoint);
    const currentLength = getLineLength(lastPoint, currentPoint);

    let assistedPoint = currentPoint;
    let foundParallel: typeof parallelAssist = null;
    let foundLength: typeof lengthAssist = null;

    // Check existing lines for parallel and length assistance
    for (let i = 0; i < currentPolygon.length - 1; i++) {
      const lineStart = currentPolygon[i] as [number, number];
      const lineEnd = currentPolygon[i + 1] as [number, number];
      const lineAngle = getLineAngle(lineStart, lineEnd);
      const lineLength = getLineLength(lineStart, lineEnd);

      // Check for parallel assistance (only when Shift+P is held)
      if (!foundParallel && isParallelAssistEnabled && areAnglesParallel(currentAngle, lineAngle)) {
        // Determine which direction (original or opposite) is closer to user's intent
        const oppositeAngle = lineAngle + Math.PI;
        
        // Calculate angle differences handling wrap-around
        const getAngleDifference = (a1: number, a2: number) => {
          let diff = Math.abs(a1 - a2);
          if (diff > Math.PI) diff = 2 * Math.PI - diff;
          return diff;
        };
        
        const angleDiff1 = getAngleDifference(currentAngle, lineAngle);
        const angleDiff2 = getAngleDifference(currentAngle, oppositeAngle);
        
        // Use the angle that's closer to the user's intended direction
        const bestAngle = angleDiff1 < angleDiff2 ? lineAngle : oppositeAngle;
        
        foundParallel = { referenceLineIndex: i, angle: bestAngle };
        // Adjust point to be exactly parallel in the correct direction
        assistedPoint = getPointAtDistanceAndAngle(lastPoint, currentLength, bestAngle);
      }

      // Check for length assistance (only when Shift+L is held)
      if (!foundLength && isLengthAssistEnabled && Math.abs(currentLength - lineLength) / lineLength < 0.1) {
        foundLength = { referenceLineIndex: i, length: lineLength };
        // Adjust point to be exactly the same length
        const direction = getLineAngle(lastPoint, assistedPoint);
        assistedPoint = getPointAtDistanceAndAngle(lastPoint, lineLength, direction);
      }
    }

    setParallelAssist(foundParallel);
    setLengthAssist(foundLength);

    return assistedPoint;
  }, [currentPolygon, getLineAngle, getLineLength, areAnglesParallel, getPointAtDistanceAndAngle, isParallelAssistEnabled, isLengthAssistEnabled]);

  // Clear assistance state when drawing stops
  useEffect(() => {
    if (!isDrawing) {
      setParallelAssist(null);
      setLengthAssist(null);
    }
  }, [isDrawing]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle hotkeys if we're drawing
      if (!isDrawing) return;

      // Undo last point (Cmd/Ctrl + Z)
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        if (currentPolygon.length > 0) {
          setCurrentPolygon(prev => prev.slice(0, -1));
        }
        return;
      }

      // Force horizontal line (Shift + H)
      if (e.shiftKey && e.key.toLowerCase() === 'h') {
        e.preventDefault();
        setIsHorizontalConstrained(true);
        setIsVerticalConstrained(false);
        setIsParallelAssistEnabled(false);
        setIsLengthAssistEnabled(false);
        return;
      }

      // Force vertical line (Shift + V)
      if (e.shiftKey && e.key.toLowerCase() === 'v') {
        e.preventDefault();
        setIsVerticalConstrained(true);
        setIsHorizontalConstrained(false);
        setIsParallelAssistEnabled(false);
        setIsLengthAssistEnabled(false);
        return;
      }

      // Enable parallel assist (Shift + P)
      if (e.shiftKey && e.key.toLowerCase() === 'p') {
        e.preventDefault();
        setIsParallelAssistEnabled(true);
        setIsHorizontalConstrained(false);
        setIsVerticalConstrained(false);
        return;
      }

      // Enable length assist (Shift + L)
      if (e.shiftKey && e.key.toLowerCase() === 'l') {
        e.preventDefault();
        setIsLengthAssistEnabled(true);
        setIsHorizontalConstrained(false);
        setIsVerticalConstrained(false);
        return;
      }

      // Clear constraints on other keys
      if (e.key === 'Escape') {
        setIsHorizontalConstrained(false);
        setIsVerticalConstrained(false);
        setIsParallelAssistEnabled(false);
        setIsLengthAssistEnabled(false);
        setParallelAssist(null);
        setLengthAssist(null);
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      // Clear constraints when shift is released
      if (!e.shiftKey) {
        setIsHorizontalConstrained(false);
        setIsVerticalConstrained(false);
        setIsParallelAssistEnabled(false);
        setIsLengthAssistEnabled(false);
        setParallelAssist(null);
        setLengthAssist(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, [isDrawing, currentPolygon.length]);

  // Find snap points near cursor
  const findSnapPoint = useCallback(
    (mousePoint: [number, number]) => {
      const snapThreshold = Math.max(4, Math.min(10, 8 / scale)); // Much tighter snapping precision
      let bestSnap: { point: [number, number]; type: 'vertex' | 'edge'; areaId?: number } | null =
        null;
      let bestDistance = snapThreshold;

      // Check all existing areas for snap opportunities
      existingAreas.forEach((area) => {
        const areaPoints = area.points;

        // Check vertices (corner points)
        areaPoints.forEach((point) => {
          // Handle both array [x,y] and object {x,y} formats
          const pointCoord: [number, number] = Array.isArray(point)
            ? [point[0], point[1]]
            : [point.x, point.y];
          const dist = distance(mousePoint, pointCoord);

          if (dist < bestDistance) {
            bestDistance = dist;
            bestSnap = { point: pointCoord, type: 'vertex', areaId: area.id };
          }
        });

        // Check edges (lines between points)
        for (let i = 0; i < areaPoints.length; i++) {
          // Handle both array [x,y] and object {x,y} formats
          const startPoint = areaPoints[i];
          const endPoint = areaPoints[(i + 1) % areaPoints.length];

          const start: [number, number] = Array.isArray(startPoint)
            ? [startPoint[0], startPoint[1]]
            : [startPoint.x, startPoint.y];
          const end: [number, number] = Array.isArray(endPoint)
            ? [endPoint[0], endPoint[1]]
            : [endPoint.x, endPoint.y];

          const dist = distanceToLineSegment(mousePoint, start, end);
          if (dist < bestDistance) {
            const snapPoint = closestPointOnLineSegment(mousePoint, start, end);
            bestDistance = dist;
            bestSnap = { point: snapPoint, type: 'edge', areaId: area.id };
          }
        }
      });

      return bestSnap;
    },
    [existingAreas, scale]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      if (!svgRef.current || !groupRef.current) return;

      // Get SVG coordinate system point
      const svg = svgRef.current;
      const pt = svg.createSVGPoint();
      pt.x = e.clientX;
      pt.y = e.clientY;
      const svgPoint = pt.matrixTransform(svg.getScreenCTM()?.inverse());

      // Transform to account for group transform
      const groupTransform = groupRef.current.transform.baseVal.consolidate();
      if (groupTransform) {
        const transformedPt = svg.createSVGPoint();
        transformedPt.x = svgPoint.x;
        transformedPt.y = svgPoint.y;
        const finalPoint = transformedPt.matrixTransform(groupTransform.matrix.inverse());
        svgPoint.x = finalPoint.x;
        svgPoint.y = finalPoint.y;
      }

      let mousePoint: [number, number] = [svgPoint.x, svgPoint.y];
      
      // Apply line constraints if we have a previous point
      if (currentPolygon.length > 0) {
        const lastPoint = currentPolygon[currentPolygon.length - 1] as [number, number];
        
        if (isHorizontalConstrained) {
          mousePoint = [svgPoint.x, lastPoint[1]];
        } else if (isVerticalConstrained) {
          mousePoint = [lastPoint[0], svgPoint.y];
        } else {
          // Apply geometric assistance when not using manual constraints
          mousePoint = findGeometricAssist(mousePoint);
        }
      }
      
      setHoveredPoint(mousePoint);

      // Find potential snap points (but don't override geometric assistance)
      if (!parallelAssist && !lengthAssist) {
        const snapPoint = findSnapPoint(mousePoint);
        setSnapInfo(snapPoint);
      } else {
        setSnapInfo(null);
      }
    },
    [svgRef, groupRef, findSnapPoint, existingAreas, currentPolygon, isHorizontalConstrained, isVerticalConstrained, findGeometricAssist, parallelAssist, lengthAssist, isParallelAssistEnabled, isLengthAssistEnabled]
  );

  const handleCanvasClick = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      if (!svgRef.current || !groupRef.current) return;

      // Get SVG coordinate system point (same logic as MainContentArea)
      const svg = svgRef.current;
      const pt = svg.createSVGPoint();
      pt.x = e.clientX;
      pt.y = e.clientY;
      const svgPoint = pt.matrixTransform(svg.getScreenCTM()?.inverse());

      // Transform to account for group transform
      const groupTransform = groupRef.current.transform.baseVal.consolidate();
      if (groupTransform) {
        const transformedPt = svg.createSVGPoint();
        transformedPt.x = svgPoint.x;
        transformedPt.y = svgPoint.y;
        const finalPoint = transformedPt.matrixTransform(groupTransform.matrix.inverse());
        svgPoint.x = finalPoint.x;
        svgPoint.y = finalPoint.y;
      }

      // Apply line constraints if we have a previous point
      let constrainedPoint: [number, number] = [svgPoint.x, svgPoint.y];
      if (currentPolygon.length > 0) {
        const lastPoint = currentPolygon[currentPolygon.length - 1] as [number, number];
        
        if (isHorizontalConstrained) {
          constrainedPoint = [svgPoint.x, lastPoint[1]];
        } else if (isVerticalConstrained) {
          constrainedPoint = [lastPoint[0], svgPoint.y];
        } else {
          // Apply geometric assistance when not using manual constraints
          constrainedPoint = findGeometricAssist(constrainedPoint);
        }
      }

      // Use snap point if available, otherwise use geometrically assisted point
      const finalPoint = snapInfo ? snapInfo.point : constrainedPoint;
      const updatedPolygon = [...currentPolygon, finalPoint];
      setCurrentPolygon(updatedPolygon);

      // Auto-close polygon when clicking near first point (if we have at least 3 points)
      if (updatedPolygon.length >= 3) {
        const firstPoint = updatedPolygon[0];
        const dist = distance(finalPoint as [number, number], [firstPoint[0], firstPoint[1]]);

        if (dist < 20) {
          // Close if within 20px of first point (without storing duplicate closing vertex)
          const closedPolygon = normalizePolygon(currentPolygon);
          onAreaCreated(closedPolygon);
          setCurrentPolygon([]);
          setSnapInfo(null);
          setParallelAssist(null);
          setLengthAssist(null);
        }
      }
    },
    [currentPolygon, onAreaCreated, svgRef, groupRef, snapInfo, isHorizontalConstrained, isVerticalConstrained]
  );

  const handleKeyPress = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      setCurrentPolygon([]);
    }
  }, []);

  // Set up wheel event listener with passive: false to allow preventDefault
  React.useEffect(() => {
    const svg = svgRef.current;
    if (!svg || !onWheelZoom) return;

    const handleWheel = (e: WheelEvent) => {
      onWheelZoom(e as unknown as React.WheelEvent);
    };

    svg.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      svg.removeEventListener('wheel', handleWheel);
    };
  }, [svgRef, onWheelZoom]);

  // Set up touch event listeners with passive: false
  React.useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    const handleTouchStartEvent = (e: TouchEvent) => {
      if (onTouchStart) {
        onTouchStart(e as unknown as React.TouchEvent);
      }
    };

    const handleTouchMoveEvent = (e: TouchEvent) => {
      if (onTouchMove) {
        onTouchMove(e as unknown as React.TouchEvent);
      }
    };

    const handleTouchEndEvent = (e: TouchEvent) => {
      if (onTouchEnd) {
        onTouchEnd(e as unknown as React.TouchEvent);
      }
    };

    svg.addEventListener('touchstart', handleTouchStartEvent, { passive: false });
    svg.addEventListener('touchmove', handleTouchMoveEvent, { passive: false });
    svg.addEventListener('touchend', handleTouchEndEvent, { passive: false });

    return () => {
      svg.removeEventListener('touchstart', handleTouchStartEvent);
      svg.removeEventListener('touchmove', handleTouchMoveEvent);
      svg.removeEventListener('touchend', handleTouchEndEvent);
    };
  }, [svgRef, onTouchStart, onTouchMove, onTouchEnd]);

  React.useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [handleKeyPress]);

  React.useEffect(() => {
    if (!isDrawing) {
      setCurrentPolygon([]);
    }
  }, [isDrawing]);

  return (
    <div className="w-full h-full flex items-center justify-center">
      <svg
        ref={svgRef}
        className="w-full h-full"
        viewBox={`-40 -40 ${imageDimensions.width + 80} ${imageDimensions.height + 80}`}
        onClick={handleCanvasClick}
        onMouseMove={handleMouseMove}
        onWheel={onWheelZoom}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        preserveAspectRatio="xMidYMid meet"
        style={{ 
          cursor: 'crosshair', 
          touchAction: 'none',
          background: 'transparent'
        }}
      >
        <g ref={groupRef} transform={`translate(${position.x}, ${position.y}) scale(${scale})`}>
          {/* NO LAYOUT IMAGE - we want to see the MainContentArea underneath */}

                    {/* Existing areas - show as guides when drawing */}
          {existingAreas.map((area) => (
            <g key={area.id}>
              {/* Area outline - enhanced visibility */}
              <polygon
                points={area.points
                  .map((p) => {
                    const [x, y] = Array.isArray(p) ? [p[0], p[1]] : [p.x, p.y];
                    return `${x},${y}`;
                  })
                  .join(' ')}
                fill="rgba(34, 197, 94, 0.08)"
                stroke="#22c55e"
                strokeWidth="2"
                strokeDasharray="8,4"
                opacity="0.7"
              />
              
              {/* Area vertices - enhanced visibility */}
              {area.points.map((point, pointIndex) => {
                const [x, y] = Array.isArray(point) ? [point[0], point[1]] : [point.x, point.y];
                return (
                  <g key={pointIndex}>
                    {/* Vertex glow for better visibility */}
                    <circle
                      cx={x}
                      cy={y}
                      r="6"
                      fill="rgba(34, 197, 94, 0.3)"
                      stroke="none"
                    />
                    {/* Main vertex point */}
                    <circle
                      cx={x}
                      cy={y}
                      r="3"
                      fill="#22c55e"
                      stroke="white"
                      strokeWidth="2"
                    />
                  </g>
                );
              })}
              
              {/* Area label for identification */}
              {area.points.length >= 3 && (
                <text
                  x={area.points.reduce((sum, p) => {
                    const [x] = Array.isArray(p) ? [p[0]] : [p.x];
                    return sum + x;
                  }, 0) / area.points.length}
                  y={area.points.reduce((sum, p) => {
                    const [, y] = Array.isArray(p) ? [p[0], p[1]] : [p.x, p.y];
                    return sum + y;
                  }, 0) / area.points.length}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  className="text-xs font-medium pointer-events-none select-none"
                  fill="#22c55e"
                  stroke="white"
                  strokeWidth="3"
                  paintOrder="stroke"
                  fontSize="12"
                  opacity="0.8"
                >
                  {area.name}
                </text>
              )}
            </g>
          ))}

          {/* Reference line highlights for geometric assistance */}
          {parallelAssist && (
            <line
              x1={currentPolygon[parallelAssist.referenceLineIndex][0]}
              y1={currentPolygon[parallelAssist.referenceLineIndex][1]}
              x2={currentPolygon[parallelAssist.referenceLineIndex + 1][0]}
              y2={currentPolygon[parallelAssist.referenceLineIndex + 1][1]}
              stroke="#f59e0b"
              strokeWidth="4"
              opacity="0.7"
              strokeDasharray="none"
            />
          )}
          
          {lengthAssist && (
            <line
              x1={currentPolygon[lengthAssist.referenceLineIndex][0]}
              y1={currentPolygon[lengthAssist.referenceLineIndex][1]}
              x2={currentPolygon[lengthAssist.referenceLineIndex + 1][0]}
              y2={currentPolygon[lengthAssist.referenceLineIndex + 1][1]}
              stroke="#8b5cf6"
              strokeWidth="4"
              opacity="0.7"
              strokeDasharray="none"
            />
          )}

          {/* Current polygon being drawn */}
          {currentPolygon.length > 0 && (
            <g>
              {/* Draw committed lines between points - more prominent */}
              {currentPolygon.map((point, index) => {
                if (index === 0) return null;
                const prevPoint = currentPolygon[index - 1];
                return (
                  <g key={index}>
                    {/* Background line for better visibility */}
                    <line
                      x1={prevPoint[0]}
                      y1={prevPoint[1]}
                      x2={point[0]}
                      y2={point[1]}
                      stroke="white"
                      strokeWidth="6"
                      opacity="0.8"
                    />
                    {/* Main line */}
                    <line
                      x1={prevPoint[0]}
                      y1={prevPoint[1]}
                      x2={point[0]}
                      y2={point[1]}
                      stroke="#3b82f6"
                      strokeWidth="3"
                      strokeDasharray="8,4"
                    />
                  </g>
                );
              })}

              {/* Preview line from last point to cursor - rubber band effect */}
              {hoveredPoint && currentPolygon.length > 0 && (
                <g>
                  {/* Background preview line */}
                  <line
                    x1={currentPolygon[currentPolygon.length - 1][0]}
                    y1={currentPolygon[currentPolygon.length - 1][1]}
                    x2={snapInfo ? snapInfo.point[0] : hoveredPoint[0]}
                    y2={snapInfo ? snapInfo.point[1] : hoveredPoint[1]}
                    stroke="white"
                    strokeWidth="4"
                    opacity="0.6"
                  />
                  {/* Main preview line */}
                  <line
                    x1={currentPolygon[currentPolygon.length - 1][0]}
                    y1={currentPolygon[currentPolygon.length - 1][1]}
                    x2={snapInfo ? snapInfo.point[0] : hoveredPoint[0]}
                    y2={snapInfo ? snapInfo.point[1] : hoveredPoint[1]}
                    stroke="#3b82f6"
                    strokeWidth="2"
                    strokeDasharray="4,4"
                    opacity="0.7"
                  />
                </g>
              )}

              {/* Draw points - bigger and more visible */}
              {currentPolygon.map((point, index) => (
                <g key={index}>
                  {/* Outer glow for visibility */}
                  <circle
                    cx={point[0]}
                    cy={point[1]}
                    r="10"
                    fill={index === 0 ? 'rgba(34, 197, 94, 0.3)' : 'rgba(59, 130, 246, 0.3)'}
                    stroke="none"
                  />
                  {/* Main point */}
                  <circle
                    cx={point[0]}
                    cy={point[1]}
                    r="6"
                    fill={index === 0 ? '#22c55e' : '#3b82f6'}
                    stroke="white"
                    strokeWidth="3"
                  />
                  {/* Inner highlight */}
                  <circle cx={point[0]} cy={point[1]} r="3" fill="white" opacity="0.6" />
                </g>
              ))}

              {/* Show close hint and preview line when near first point */}
              {currentPolygon.length >= 3 &&
                hoveredPoint &&
                (() => {
                  const firstPoint = currentPolygon[0];
                  const lastPoint = currentPolygon[currentPolygon.length - 1];
                  const dist = Math.sqrt(
                    Math.pow(hoveredPoint[0] - firstPoint[0], 2) +
                      Math.pow(hoveredPoint[1] - firstPoint[1], 2)
                  );
                  const canClose = dist < 20;

                  return (
                    <g key="close-hint">
                      {/* Preview closing line when near first point */}
                      {canClose && (
                        <g>
                          {/* Background closing line */}
                          <line
                            x1={lastPoint[0]}
                            y1={lastPoint[1]}
                            x2={firstPoint[0]}
                            y2={firstPoint[1]}
                            stroke="white"
                            strokeWidth="4"
                            opacity="0.8"
                          />
                          {/* Main closing line */}
                          <line
                            x1={lastPoint[0]}
                            y1={lastPoint[1]}
                            x2={firstPoint[0]}
                            y2={firstPoint[1]}
                            stroke="#22c55e"
                            strokeWidth="3"
                            strokeDasharray="6,3"
                          />
                        </g>
                      )}

                      {/* Pulsing outer ring */}
                      <circle
                        cx={firstPoint[0]}
                        cy={firstPoint[1]}
                        r="20"
                        fill="none"
                        stroke="#22c55e"
                        strokeWidth="2"
                        strokeDasharray="4,4"
                        opacity={canClose ? 0.8 : 0.4}
                      >
                        <animate
                          attributeName="r"
                          values="15;25;15"
                          dur="2s"
                          repeatCount="indefinite"
                        />
                        <animate
                          attributeName="opacity"
                          values={canClose ? '0.8;0.3;0.8' : '0.4;0.2;0.4'}
                          dur="2s"
                          repeatCount="indefinite"
                        />
                      </circle>
                      {/* Inner ring */}
                      <circle
                        cx={firstPoint[0]}
                        cy={firstPoint[1]}
                        r="15"
                        fill="none"
                        stroke="#22c55e"
                        strokeWidth="3"
                        strokeDasharray="6,3"
                        opacity={canClose ? 1 : 0.6}
                      />
                    </g>
                  );
                })()}
            </g>
          )}

          {/* Snap indicators - enhanced visibility */}
          {snapInfo && (
            <g>
              {/* Outer pulsing ring */}
              <circle
                cx={snapInfo.point[0]}
                cy={snapInfo.point[1]}
                r="15"
                fill="none"
                stroke={snapInfo.type === 'vertex' ? '#f97316' : '#eab308'}
                strokeWidth="2"
                opacity="0.7"
              >
                <animate attributeName="r" values="12;18;12" dur="1.5s" repeatCount="indefinite" />
                <animate
                  attributeName="opacity"
                  values="0.8;0.4;0.8"
                  dur="1.5s"
                  repeatCount="indefinite"
                />
              </circle>
              {/* Middle ring */}
              <circle
                cx={snapInfo.point[0]}
                cy={snapInfo.point[1]}
                r="12"
                fill="none"
                stroke={snapInfo.type === 'vertex' ? '#f97316' : '#eab308'}
                strokeWidth="2"
                strokeDasharray="4,2"
                opacity="0.8"
              />
              {/* Inner solid circle */}
              <circle
                cx={snapInfo.point[0]}
                cy={snapInfo.point[1]}
                r="8"
                fill={snapInfo.type === 'vertex' ? '#f97316' : '#eab308'}
                stroke="white"
                strokeWidth="3"
                opacity="0.9"
              />
              {/* Center highlight */}
              <circle
                cx={snapInfo.point[0]}
                cy={snapInfo.point[1]}
                r="4"
                fill="white"
                opacity="0.8"
              />
            </g>
          )}

          {/* Cursor guide - enhanced visibility */}
          {hoveredPoint && !snapInfo && (
            <g>
              {/* Outer guide ring */}
              <circle
                cx={hoveredPoint[0]}
                cy={hoveredPoint[1]}
                r="8"
                fill="none"
                stroke="#3b82f6"
                strokeWidth="1"
                strokeDasharray="2,2"
                opacity="0.6"
              />
              {/* Main cursor point */}
              <circle
                cx={hoveredPoint[0]}
                cy={hoveredPoint[1]}
                r="4"
                fill="#3b82f6"
                stroke="white"
                strokeWidth="2"
                opacity="0.8"
              />
            </g>
          )}

          {/* Snap indicators */}
          {snapInfo && (
            <g>
              <circle
                cx={snapInfo.point[0]}
                cy={snapInfo.point[1]}
                r="8"
                fill={snapInfo.type === 'vertex' ? 'orange' : 'yellow'}
                stroke="white"
                strokeWidth="2"
                opacity="0.8"
              />
              <circle
                cx={snapInfo.point[0]}
                cy={snapInfo.point[1]}
                r="12"
                fill="none"
                stroke={snapInfo.type === 'vertex' ? 'orange' : 'yellow'}
                strokeWidth="1"
                strokeDasharray="2,2"
                opacity="0.6"
              />
            </g>
          )}

          {/* Cursor guide */}
          {hoveredPoint && (
            <circle
              cx={hoveredPoint[0]}
              cy={hoveredPoint[1]}
              r="3"
              fill="rgba(0,0,255,0.3)"
              stroke="blue"
              strokeWidth="1"
            />
          )}

          {/* Constraint indicators */}
          {currentPolygon.length > 0 && hoveredPoint && (isHorizontalConstrained || isVerticalConstrained || isParallelAssistEnabled || isLengthAssistEnabled) && (
            <g>
              {/* Constraint line preview */}
              <line
                x1={currentPolygon[currentPolygon.length - 1][0]}
                y1={currentPolygon[currentPolygon.length - 1][1]}
                x2={hoveredPoint[0]}
                y2={hoveredPoint[1]}
                stroke={isParallelAssistEnabled ? "#f59e0b" : isLengthAssistEnabled ? "#8b5cf6" : "#f59e0b"}
                strokeWidth="3"
                strokeDasharray="6,3"
                opacity="0.8"
              />
              
              {/* Constraint type indicator */}
              <text
                x={hoveredPoint[0] + 10}
                y={hoveredPoint[1] - 10}
                fill={isParallelAssistEnabled ? "#f59e0b" : isLengthAssistEnabled ? "#8b5cf6" : "#f59e0b"}
                stroke="white"
                strokeWidth="2"
                paintOrder="stroke"
                fontSize="12"
                className="font-medium pointer-events-none select-none"
              >
                {isHorizontalConstrained ? 'H' : isVerticalConstrained ? 'V' : isParallelAssistEnabled ? '∥' : isLengthAssistEnabled ? '≈' : ''}
              </text>
            </g>
          )}

          {/* Geometric assistance indicators */}
          {currentPolygon.length > 0 && hoveredPoint && (parallelAssist || lengthAssist) && (
            <g>
              {/* Assistance line preview */}
              <line
                x1={currentPolygon[currentPolygon.length - 1][0]}
                y1={currentPolygon[currentPolygon.length - 1][1]}
                x2={hoveredPoint[0]}
                y2={hoveredPoint[1]}
                stroke={parallelAssist ? "#f59e0b" : "#8b5cf6"}
                strokeWidth="3"
                strokeDasharray="4,2"
                opacity="0.9"
              />
              
              {/* Assistance type indicators */}
              {parallelAssist && (
                <text
                  x={hoveredPoint[0] + 10}
                  y={hoveredPoint[1] - 25}
                  fill="#f59e0b"
                  stroke="white"
                  strokeWidth="2"
                  paintOrder="stroke"
                  fontSize="11"
                  className="font-medium pointer-events-none select-none"
                >
                  ∥ Parallel
                </text>
              )}
              
              {lengthAssist && (
                <text
                  x={hoveredPoint[0] + 10}
                  y={hoveredPoint[1] - (parallelAssist ? 10 : 25)}
                  fill="#8b5cf6"
                  stroke="white"
                  strokeWidth="2"
                  paintOrder="stroke"
                  fontSize="11"
                  className="font-medium pointer-events-none select-none"
                >
                  ≈ Same Length
                </text>
              )}
            </g>
          )}

        </g>
      </svg>
    </div>
  );
};

/**
 * Ultra-minimal area tags overlay - just essential functionality
 */
export const EnhancedAreaTagsOverlay: React.FC<EnhancedAreaTagsOverlayProps> = ({
  selectedLayoutId,
  projectId,
  imageDimensions,
  position,
  scale,
  svgRef,
  groupRef,
  onWheelZoom,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  onTextEditingModeChange,
  onSaveTextChanges,
  onCancelTextChanges,
  onPolygonEditingModeChange,
  pendingPolygonChanges = new Map(),
}) => {
  // State management
  const [editMode, setEditMode] = useState<'view' | 'create' | 'textEdit' | 'polygonEdit'>('view');
  const [showAreaForm, setShowAreaForm] = useState(false);
  const [areaName, setAreaName] = useState('');
  const [detectedMarkers, setDetectedMarkers] = useState<PhotoMarker[]>([]);
  const [manualMarkerIds, setManualMarkerIds] = useState('');
  const [currentPolygon, setCurrentPolygon] = useState<number[][]>([]);

  // Data hooks
  const { data: areaTags, isLoading } = useAreaTagsOptimistic(selectedLayoutId, projectId);
  const { data: photoMarkers = [] } = usePhotoMarkers(selectedLayoutId);
  const { createAreaTag } = useAreaTagMutations(selectedLayoutId, projectId);

  // Handle area creation - now shows form with detected markers
  const handleAreaCreated = useCallback(
    async (points: number[][]) => {
      const newPolygon = normalizePolygonPoints(points as [number, number][]);
      const hasOverlap = areaTags.some((area) => {
        const existing = normalizePolygonPoints(area.points as Array<[number, number] | { x: number; y: number }>);
        return polygonsOverlap(newPolygon, existing);
      });
      if (hasOverlap) {
        alert('This area overlaps an existing area. Please draw a non-overlapping polygon.');
        return;
      }

      // Detect markers within the polygon
      const markersInPolygon = findMarkersInPolygon(photoMarkers, points);
      setDetectedMarkers(markersInPolygon);
      setCurrentPolygon(points);
      setShowAreaForm(true);
      setEditMode('view'); // Exit create mode
    },
    [photoMarkers]
  );

  // Handle form submission
  const handleFormSubmit = useCallback(async () => {
    if (!areaName.trim()) return;

    // Parse manual marker IDs and find those markers
    const manualIds = manualMarkerIds
      .split(',')
      .map((id) => parseInt(id.trim()))
      .filter((id) => !isNaN(id));

    const manualMarkers = photoMarkers.filter((marker) => manualIds.includes(marker.number));

    // Combine detected and manual markers
    const allMarkers = [...detectedMarkers, ...manualMarkers];

    // Get all photo IDs from the markers
    const allPhotoIds = allMarkers.flatMap((marker) => marker.photos.map((photo) => photo.id));

    // Remove duplicates
    const uniquePhotoIds = [...new Set(allPhotoIds)];

    // Convert number[][] to Point[] format expected by API
    const formattedPoints = currentPolygon.map(([x, y]) => ({ x, y }));

    try {
      await createAreaTag.mutateAsync({
        name: areaName.trim(),
        layout: selectedLayoutId,
        coordinates: formattedPoints,
        photos: uniquePhotoIds,
      });

      // Reset form
      setShowAreaForm(false);
      setAreaName('');
      setManualMarkerIds('');
      setDetectedMarkers([]);
      setCurrentPolygon([]);
    } catch (error) {
      console.error('❌ Failed to create area:', error);
      alert('Failed to create area. Please try again.');
    }
  }, [
    createAreaTag,
    selectedLayoutId,
    areaName,
    currentPolygon,
    detectedMarkers,
    manualMarkerIds,
    photoMarkers,
  ]);

  return (
    <div className="absolute inset-0 z-10 pointer-events-none">
      {/* Drawing Overlay - only render when actively drawing */}
      {editMode === 'create' && (
        <div className="w-full h-full flex items-center justify-center pointer-events-auto">
          <DrawingOverlay
            isDrawing={true}
            imageDimensions={imageDimensions}
            position={position}
            scale={scale}
            svgRef={svgRef}
            groupRef={groupRef}
            existingAreas={areaTags}
            onAreaCreated={handleAreaCreated}
            onWheelZoom={onWheelZoom}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          />
        </div>
      )}

      {/* Minimal Floating Controls - Always accessible */}
      <div className="absolute top-4 left-4 z-20 pointer-events-auto">
        <div className="flex flex-col gap-2">
          <Button
            variant={editMode === 'create' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setEditMode(editMode === 'create' ? 'view' : 'create')}
            className="shadow-lg"
          >
            {editMode === 'create' ? (
              <>
                <X className="h-4 w-4 mr-2" />
                Cancel Drawing
              </>
            ) : (
              <>
                <Plus className="h-4 w-4 mr-2" />
                Create Area
              </>
            )}
          </Button>

          {/* Text Editing Mode Button - Only show if areas exist and not in create mode */}
          {areaTags.length > 0 && editMode !== 'create' && (
            <Button
              variant={editMode === 'textEdit' ? 'default' : 'outline'}
              size="sm"
              onClick={() => {
                if (editMode === 'textEdit') {
                  setEditMode('view');
                  onTextEditingModeChange?.(false, null);
                } else {
                  setEditMode('textEdit');
                  onTextEditingModeChange?.(true, null);
                }
              }}
              className="shadow-lg"
            >
              <Type className="h-4 w-4 mr-2" />
              {editMode === 'textEdit' ? 'Exit Label Edit' : 'Edit Labels'}
            </Button>
          )}

          {/* Polygon Editing Button - Only show when areas exist and not in create mode */}
          {areaTags.length > 0 && editMode !== 'create' && (
            <Button
              variant={editMode === 'polygonEdit' ? 'default' : 'outline'}
              size="sm"
              onClick={() => {
                if (editMode === 'polygonEdit') {
                  // Check if there are pending changes when exiting polygon edit mode
                  if (pendingPolygonChanges && pendingPolygonChanges.size > 0) {
                    const shouldSave = window.confirm(
                      `You have unsaved polygon changes to ${pendingPolygonChanges.size} area(s). Do you want to save them before exiting?\n\nClick OK to save and exit, or Cancel to discard changes and exit.`
                    );
                    
                    if (shouldSave) {
                      onSaveTextChanges?.(); // Save changes (this handles both text and polygon)
                    } else {
                      onCancelTextChanges?.(); // Discard changes
                    }
                  }
                  // Exit polygon editing mode
                  setEditMode('view');
                  onPolygonEditingModeChange?.(false);
                } else {
                  // Enter polygon editing mode
                  setEditMode('polygonEdit');
                  onPolygonEditingModeChange?.(true);
                }
              }}
              className="shadow-lg"
            >
              <Edit3 className="h-4 w-4 mr-2" />
              {editMode === 'polygonEdit' ? (pendingPolygonChanges && pendingPolygonChanges.size > 0 ? `Save & Exit (${pendingPolygonChanges.size} changes)` : 'Exit Polygon Edit') : 'Edit Polygons'}
            </Button>
          )}

          {/* Text Editing Instructions - Only show when in text edit mode */}
          {editMode === 'textEdit' && (
            <Card className="shadow-lg">
              <CardContent className="p-2">
                                  <div className="text-xs space-y-1">
                    <div className="font-medium text-gray-700 mb-1">Text Editing:</div>
                    <div>• Click or tap any area polygon</div>
                    <div>• Label dialog opens with current name</div>
                    <div>• Save updates immediately and syncs to server</div>
                    <div>• Cancel closes without changing label</div>
                    <div>• Use "Exit Label Edit" when done</div>
                  </div>
              </CardContent>
            </Card>
          )}

          {/* Polygon Editing Instructions - Only show when in polygon edit mode */}
          {editMode === 'polygonEdit' && (
            <Card className="shadow-lg">
              <CardContent className="p-2">
                <div className="text-xs space-y-1">
                  <div className="font-medium text-gray-700 mb-1">Polygon Editing:</div>
                  <div>• Click any polygon to select it</div>
                  <div>• <span className="text-blue-600">Blue numbered circles</span>: Drag to move vertices</div>
                  <div>• <span className="text-yellow-500">Yellow circles</span>: Snap to nearby vertices/edges</div>
                  <div>• <span className="font-medium text-orange-600">Changes are saved when you exit</span></div>
                  <div>• Press ESC to finish editing</div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Hotkeys Guide - Only show when drawing */}
          {editMode === 'create' && (
            <Card className="shadow-lg">
              <CardContent className="p-2">
                <div className="text-xs space-y-1">
                  <div className="font-medium text-gray-700 mb-1">Drawing Assistance:</div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Undo point:</span>
                    <code className="text-blue-600 font-mono">Cmd+Z</code>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Horizontal:</span>
                    <code className="text-blue-600 font-mono">Shift+H</code>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Vertical:</span>
                    <code className="text-blue-600 font-mono">Shift+V</code>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Parallel:</span>
                    <code className="text-orange-600 font-mono">Shift+P</code>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Same Length:</span>
                    <code className="text-purple-600 font-mono">Shift+L</code>
                  </div>
                  <div className="text-xs text-gray-500 mt-2 pt-1 border-t">
                    <div>• <span className="text-orange-600">Orange</span>: Parallel lines</div>
                    <div>• <span className="text-purple-600">Purple</span>: Same length</div>
                    <div>• Auto-snaps when close</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Area Count Badge - Only show if areas exist */}
      {areaTags.length > 0 && (
        <div className="absolute top-4 right-4 z-20 pointer-events-auto">
          <Badge variant="secondary" className="shadow-lg px-3 py-1">
            <MapPin className="h-3 w-3 mr-1" />
            {areaTags.length} area{areaTags.length !== 1 ? 's' : ''}
          </Badge>
        </div>
      )}

      {/* Area Creation Form - Shows after drawing polygon */}
      {showAreaForm && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-40 pointer-events-auto">
          <Card className="w-96 max-w-[90%] shadow-xl">
            <CardContent className="p-6 bg-white">
              <h3 className="text-lg font-semibold mb-4">Create New Area</h3>

              {/* Area Name Input */}
              <div className="mb-4">
                <Label htmlFor="area-name">Area Name</Label>
                <Input
                  id="area-name"
                  type="text"
                  value={areaName}
                  onChange={(e) => setAreaName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && areaName.trim()) {
                      handleFormSubmit();
                    }
                  }}
                  placeholder="Enter area name..."
                  className="mt-1"
                  autoFocus
                />
              </div>

              {/* Detected Markers */}
              {detectedMarkers.length > 0 && (
                <div className="mb-4">
                  <Label className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Detected Markers ({detectedMarkers.length})
                  </Label>
                  <div className="mt-2 p-3 bg-gray-50 rounded-md">
                    <div className="flex flex-wrap gap-1">
                      {detectedMarkers.map((marker) => (
                        <Badge key={marker.id} variant="outline" className="text-xs">
                          #{marker.number}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Manual Marker IDs Input */}
              <div className="mb-6">
                <Label htmlFor="manual-markers">Additional Marker IDs (optional)</Label>
                <Input
                  id="manual-markers"
                  type="text"
                  value={manualMarkerIds}
                  onChange={(e) => setManualMarkerIds(e.target.value)}
                  placeholder="e.g., 25, 36, 42"
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Comma-separated marker numbers to include manually
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowAreaForm(false);
                    setAreaName('');
                    setManualMarkerIds('');
                    setDetectedMarkers([]);
                    setCurrentPolygon([]);
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button onClick={handleFormSubmit} disabled={!areaName.trim()} className="flex-1">
                  Create Area
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Loading Overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-white/50 backdrop-blur-sm flex items-center justify-center z-30">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <span className="text-sm">Loading areas...</span>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};
